import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { CopyToClipboardButton } from "@freightsimple/ui-core/Buttons/CopyToClipboardButton";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { CopyableLinkBox } from "@/Shared/Components/CustomComponents/CopyableLinkBox";
import { rootUrl } from "@/Environment";
import { useEffect, useState } from "react";

// "Invite a colleague to enter the company credit card": fetches a share
// authorization when opened and shows the copyable link. Used from the
// Credit Cards screen and the booking payment step. Replaces the legacy
// imperative modal.
export function CreditCardsShareDialog(props: DialogBaseProps) {
  const paymentMethodsApi = usePaymentMethodsApi();
  const [authorizationId, setAuthorizationId] = useState<string | undefined>();

  useEffect(() => {
    if (!props.open || authorizationId !== undefined) {
      return;
    }
    paymentMethodsApi.getAddCreditCardAuthorizationId().then(
      (response) => setAuthorizationId(response.addCreditCardAuthorizationId),
      (e) => console.error(e),
    );
  }, [props.open]);

  const url = `${rootUrl()}add-credit-card?id=${authorizationId}`;

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Invite a colleague to enter the company credit card"
      width="wide"
      footer={
        <Button primary onClick={() => props.onOpenChange?.(false)}>
          Done
        </Button>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        {authorizationId === undefined && <Spinner size={32} />}
        {authorizationId !== undefined && (
          <Stack gap={4} align="start">
            <Stack gap={2} align="start">
              <T size="base" color="secondary">
                If you don&apos;t have access to the credit card, you can send
                this link to a colleague, and they can enter the credit card
                with it.
              </T>
              <T size="base" color="secondary">
                You can copy this link and send it to them via Email, Slack or
                similar.
              </T>
            </Stack>
            {/* The box carries the link, the button carries the affordance —
                the bare value with a trailing copy icon read as prose and the
                icon went unnoticed. */}
            <Row gap={3} shrinkable>
              <CopyableLinkBox url={url} />
              <CopyToClipboardButton
                data={url}
                label="Add credit card link"
                size="link"
              >
                Click here to copy to clipboard
              </CopyToClipboardButton>
            </Row>
          </Stack>
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}
